import cn from "classnames";
import { easeInOut, motion } from "framer-motion";
import AnimatedHeight from "../AnimatedHeight";
import { Text, TEXT_VARIANTS } from "../Text";
import { Callout } from "./Callout";
import { MobileTimelineItem } from "./MobileTimelineItem";

export type CarouselTimelineItemProps = {
  eyebrow: string;
  title: string;
  description?: string;
  calloutTitle?: string;
  calloutDescription?: string;
  isActive: boolean;
  isTimerActive: boolean;
  onClick: () => void;
  imageSrc: string;
  timerDuration?: number;
}

// The clickedProgressBarVariants are outside the component to avoid re-rendering
const clickedProgressBarVariants = {
  inactive: { width: 0, transition: { duration: 0.1 } },
  active: { width: '100%', transition: { duration: 0.1, ease: easeInOut } },
}

// The heading animates the item is active
const headingStyles = {
  active: cn(TEXT_VARIANTS.h1, 'pb-xs transition-all duration-400 text-primary'),
  inactive: cn(TEXT_VARIANTS.p1, 'transition-all duration-400 text-primary-inactive'),
}

/**
 * Carousel Timeline Item component is meant to function both on a timer as well as a clickable item.
 * As such, it has two sets of animation variants for the progress bar, a slower one for the timer 
 * and a faster one for the clickable item.
 * 
 * The duration of the timer is configurable, and defaults to 12 seconds.
 */
export const CarouselTimelineItem = ({
  ...props
}: CarouselTimelineItemProps) => {
  const {
    eyebrow,
    title,
    description,
    calloutTitle,
    calloutDescription,
    isActive,
    isTimerActive,
    onClick,
    timerDuration = 12
  } = props;

  // These variants are defined within the component since the duration is adjustable via props
  const timerProgressBarVariants = {
    inactive: { width: 0, transition: { duration: 0.1, ease: 'linear' } },
    active: { width: '100%', transition: { duration: timerDuration, ease: 'linear' } },
  }
  const progressBarVariants = isTimerActive ? timerProgressBarVariants : clickedProgressBarVariants;

  return (
    <>
      {/* Desktop Item */}
      <button className="hidden md:flex flex-col cursor-pointer text-left pt-lg" onClick={onClick}>
        <Text type="micro1" className={cn(isActive ? 'text-secondary' : 'text-secondary-inactive', 'mb-xs')}>{eyebrow}</Text>
        <p className={cn(isActive ? headingStyles.active : headingStyles.inactive)}>{title}</p>
        <AnimatedHeight
          isExpanded={isActive}
          className="flex flex-col gap-y-lg"
        >
          <Text type="p2" className="text-primary">{description}</Text>
          {calloutTitle && calloutDescription && (
            <Callout title={calloutTitle} description={calloutDescription} />
          )}
        </AnimatedHeight>
        {/* Progress bar */}
        <div className="mt-lg relative w-full h-[1px] border-l0 z-0">
          <motion.div
            className="absolute inset-0 h-[1px] z-1 border-active"
            initial={{ width: 0 }}
            variants={progressBarVariants}
            animate={isActive ? 'active' : 'inactive'}
          />
        </div>
      </button>
      {/* Mobile Item */}
      <MobileTimelineItem {...props} />
    </>
  );
};
